/**
 * File-based workspace operations (export/import to/from disk)
 * Supports encrypted and plain JSON formats
 */

import { WORKSPACE_CONFIG } from "../constants.js";
import {
  serializeWorkspace,
  deserializeWorkspace,
} from "../core/serialization.js";
import {
  downloadFile,
  downloadFileAlternative,
  readFile,
  openFilePicker,
} from "../utils/fileUtils.js";
import {
  encryptDesktop,
  decryptDesktop,
  checkEncryption,
  isPlainJson,
} from "../utils/encryption.js";

/**
 * Exports the current desktop to a file (encrypted or plain)
 * @param {Object} chartsStore - The charts store state
 * @param {string} filename - Optional filename (defaults to timestamp)
 * @param {string} password - Optional password (blank = obfuscation only, null = plain JSON)
 * @returns {Promise<{success: boolean, encrypted: boolean, passwordProtected: boolean}>} - Export result
 */
export const exportWorkspace = async (
  chartsStore,
  filename = null,
  password = "",
) => {
  try {
    const desktopData = await serializeWorkspace(chartsStore);

    const defaultFilename =
      filename ||
      `${WORKSPACE_CONFIG.DEFAULT_FILENAME_PREFIX}-${new Date().toISOString().slice(0, 10)}${WORKSPACE_CONFIG.DESKTOP_EXTENSION}`;

    let content;
    let encrypted = false;
    let passwordProtected = false;

    // Always encrypt (with or without password)
    content = await encryptDesktop(
      JSON.stringify(desktopData, null, 2),
      password,
    );
    encrypted = true;
    passwordProtected = password && password.trim().length > 0;

    try {
      // Try the primary download method first
      await downloadFile(
        content,
        defaultFilename,
        WORKSPACE_CONFIG.FILE_MIME_TYPE,
      );
      return { success: true, encrypted, passwordProtected };
    } catch (primaryError) {
      console.warn(
        "Primary download method failed, trying alternative:",
        primaryError.message,
      );

      try {
        // If primary method fails, try the alternative
        await downloadFileAlternative(content, defaultFilename);
        return { success: true, encrypted, passwordProtected };
      } catch (alternativeError) {
        console.error(
          "Alternative download method also failed:",
          alternativeError.message,
        );
        throw primaryError; // Throw the original error
      }
    }
  } catch (error) {
    console.error("Failed to export desktop:", error);

    // Provide more specific error messages based on the error type
    if (error.message.includes("blocked")) {
      throw new Error(
        "Download bloqueado pelo navegador. Verifique as configurações ou tente outro navegador.",
      );
    } else if (error.message.includes("security policy")) {
      throw new Error(
        "Política de segurança do navegador bloqueou o download. Tente novamente ou use outro navegador.",
      );
    } else {
      throw new Error("Falha ao exportar desktop. Tente novamente.");
    }
  }
};

/**
 * Imports a desktop from a file (supports encrypted and plain JSON)
 * @param {Function} loadCharts - Function to load charts into store
 * @param {string} password - Optional password for encrypted files
 * @returns {Promise<{success: boolean, encrypted: boolean, passwordRequired: boolean}>} - Import result
 */
export const importWorkspace = async (loadCharts, password = "") => {
  try {
    console.log("Starting desktop import process...");

    // Try programmatic file picker
    const pickerResult = await openFilePicker();
    console.log("File picker result:", pickerResult);

    // If user cancelled, don't proceed
    if (pickerResult.cancelled) {
      console.log("User cancelled file picker");
      return { success: false, cancelled: true };
    }

    const file = pickerResult.file;

    if (!file) {
      console.log("No file selected");
      return { success: false };
    }

    console.log("Reading file content...");
    const content = await readFile(file);

    // Check if encrypted
    const encryptionInfo = checkEncryption(content);
    const isEncryptedFile = encryptionInfo !== null;

    let desktopData;

    if (isEncryptedFile) {
      console.log(
        "File is encrypted, password required:",
        encryptionInfo.passwordRequired,
      );

      // If password is required but not provided, throw error with file content
      if (
        encryptionInfo.passwordRequired &&
        (!password || password.trim().length === 0)
      ) {
        const error = new Error("PASSWORD_REQUIRED");
        error.fileContent = content; // Attach file content for retry
        throw error;
      }

      // Decrypt
      const decryptedJson = await decryptDesktop(content, password);
      desktopData = JSON.parse(decryptedJson);
    } else if (isPlainJson(content)) {
      // Legacy plain JSON format
      console.log("File is plain JSON (legacy format)");
      desktopData = JSON.parse(content);
    } else {
      throw new Error("Formato de arquivo inválido");
    }

    console.log("Data parsed successfully, deserializing desktop...");

    const result = deserializeWorkspace(desktopData, loadCharts);
    console.log("Desktop imported successfully:", result);

    return {
      success: result,
      encrypted: isEncryptedFile,
      passwordRequired: encryptionInfo?.passwordRequired || false,
    };
  } catch (error) {
    console.error("Failed to import desktop:", error);
    console.error("Error details:", {
      name: error.name,
      message: error.message,
      stack: error.stack,
    });
    throw error;
  }
};

/**
 * Copies desktop data to clipboard as a fallback when downloads are blocked
 * @param {Object} chartsStore - The charts store state
 * @param {string} password - Optional password (blank = obfuscation only)
 * @returns {Promise<boolean>} - Success status
 */
export const copyWorkspaceToClipboard = async (chartsStore, password = "") => {
  try {
    const desktopData = await serializeWorkspace(chartsStore);

    // Always encrypt for clipboard
    const content = await encryptDesktop(
      JSON.stringify(desktopData, null, 2),
      password,
    );

    await navigator.clipboard.writeText(content);
    return true;
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    throw new Error(
      "Não foi possível copiar para área de transferência. Verifique as permissões.",
    );
  }
};
